<template>
  <div class="statusbar" role="status" aria-label="Viewer status bar">
    <!-- Cell 1 · State + Hint -->
    <div class="sb-cell sb-state" :class="{ active: display.ledPulse }">
      <span v-if="display.loading" class="sb-loading" aria-hidden="true"></span>
      <span class="led" :class="`led--${display.tone}`" aria-hidden="true"></span>
      <span class="label" :class="`label--${display.tone}`">{{ display.label }}</span>
      <template v-if="display.hint">
        <span class="sep" aria-hidden="true">·</span>
        <span
          class="hint"
          :class="{ 'hint--warn': display.hintWarn }"
          :role="display.hintWarn ? 'alert' : null"
        >{{ display.hint }}</span>
      </template>
    </div>

    <!-- Cell 2 · View combo — replaces the removed vertical ViewSwitcher rail.
         Opens a dropdown listing three OPPOSITE PAIRS of world-fixed ortho views
         — Elevation Top/Bottom, Northings North/South, Eastings East/West —
         then a divider and Isometric. Every label names THE FACE YOU SEE.
         Hotkeys 1-6 / 0 fire without opening the menu. Rows come from
         `verificationContext.VERIFICATION_WORKSPACE_VIEWS`; nothing here
         hard-codes a view id or a key. -->
    <div class="sb-cell sb-view-cell">
      <span class="cap">View</span>
      <v-menu
        v-model="menuOpen"
        top
        offset-y
        :nudge-bottom="-4"
        :close-on-content-click="true"
        transition="slide-y-reverse-transition"
        content-class="sb-menu-popover"
      >
        <template v-slot:activator="{ on, attrs }">
          <button
            type="button"
            class="sb-combo"
            v-bind="attrs"
            :aria-expanded="menuOpen ? 'true' : 'false'"
            aria-haspopup="listbox"
            :disabled="!viewerReady || !activeViewData"
            :title="activeViewData ? `${viewLabel(activeViewData)} — ${activeViewData.sub}` : 'View'"
            v-on="on"
          >
            <span class="cg" v-html="activeViewGlyph"></span>
            <span class="vlabel">{{ activeViewData ? viewLabel(activeViewData) : '—' }}</span>
            <span class="vsub">{{ activeViewData ? activeViewData.sub : '' }}</span>
            <span class="chev" aria-hidden="true">
              <svg viewBox="0 0 10 10"><path d="M2 4l3 3 3-3" /></svg>
            </span>
          </button>
        </template>
        <div class="sb-menu sb-view-menu" role="listbox" aria-label="Choose view">
          <template v-for="(item, idx) in viewItems">
            <button
              :key="item.id"
              type="button"
              class="sb-menu-item"
              :class="{ on: activeView === item.id }"
              role="option"
              :aria-selected="activeView === item.id ? 'true' : 'false'"
              @click="requestView(item.id)"
            >
              <span class="mg" v-html="getGlyph(item.glyph)"></span>
              <span class="mname">{{ viewLabel(item) }}</span>
              <span class="msub">{{ item.sub }}</span>
              <span v-if="item.kbd" class="mkbd">{{ item.kbd }}</span>
            </button>
            <!-- Divider between the 6 orthogonal views and the isometric option -->
            <div
              v-if="isOrthoDividerAfter(idx)"
              :key="`${item.id}-divider`"
              class="sb-menu-divider"
              aria-hidden="true"
            ></div>
          </template>
        </div>
      </v-menu>

      <!-- View lock. Keeps the orthographic view square-on while still allowing
           pan and zoom, which is exactly what measuring wants: framing freedom
           without losing the projection the measurement is being read against.
           An accidental orbit off a plan view silently turns a plan reading into
           an oblique one. -->
      <button
        type="button"
        class="sb-lock"
        :class="{ on: viewLocked }"
        :aria-pressed="viewLocked ? 'true' : 'false'"
        :disabled="!viewerReady"
        :title="viewLockTitle"
        @click="toggleViewLock"
      >
        <svg viewBox="0 0 12 12" aria-hidden="true">
          <path v-if="viewLocked" d="M3.2 5.4V4a2.8 2.8 0 0 1 5.6 0v1.4" />
          <path v-else d="M3.2 5.4V4a2.8 2.8 0 0 1 5.35-1.1" />
          <rect x="2.4" y="5.4" width="7.2" height="4.6" rx="1" />
        </svg>
        <span class="lock-text">{{ viewLocked ? 'LOCKED' : 'FREE' }}</span>
      </button>
    </div>

    <!-- Cell 3 · Layers — product-specific, independent toggles (not radio).
         Three buttons answering "what do I see in the viewer right now":
           3D    — design geometry visibility
           Scan  — point cloud visibility
           Crop  — crop box chrome (mesh + handles) visibility -->
    <div class="sb-cell sb-layers">
      <span class="cap">Layers</span>
      <div class="seg" role="group" aria-label="Layers">
        <v-menu
          v-model="modelMenuOpen"
          top
          offset-y
          :nudge-bottom="-4"
          :close-on-content-click="false"
          transition="slide-y-reverse-transition"
          content-class="sb-marker-popover"
        >
          <template v-slot:activator="{ on, attrs }">
            <button
              type="button"
              v-bind="attrs"
              :class="{ on: layers.model3d }"
              :aria-pressed="layers.model3d ? 'true' : 'false'"
              :aria-expanded="modelMenuOpen ? 'true' : 'false'"
              aria-label="3D model appearance controls"
              :disabled="!viewerReady"
              title="Control 3D model appearance"
              v-on="on"
            >3D</button>
          </template>
          <div class="sb-layer-menu" aria-label="3D model appearance controls">
            <div class="sb-layer-menu__heading">
              <span>
                <strong>3D model</strong>
                <small>Tune the design geometry against the scan</small>
              </span>
              <label class="sb-marker-switch">
                <input
                  type="checkbox"
                  :checked="visualizationSettings.model.visible"
                  @change="setVisualizationSetting('model', 'visible', $event.target.checked)"
                />
                <span>{{ visualizationSettings.model.visible ? 'Shown' : 'Hidden' }}</span>
              </label>
            </div>
            <label class="sb-layer-slider">
              <span>
                <span class="sb-layer-menu__caption">Opacity</span>
                <strong>{{ Math.round(visualizationSettings.model.opacity * 100) }}%</strong>
              </span>
              <input
                type="range"
                min="0.05"
                max="1"
                step="0.05"
                aria-label="3D model opacity"
                :value="visualizationSettings.model.opacity"
                @input="setVisualizationSetting('model', 'opacity', $event.target.value)"
              />
            </label>
            <div class="sb-layer-menu__row">
              <label class="sb-layer-check">
                <input
                  type="checkbox"
                  :checked="visualizationSettings.model.tintEnabled"
                  @change="setVisualizationSetting('model', 'tintEnabled', $event.target.checked)"
                />
                <span>Tint</span>
              </label>
              <label class="sb-color-control">
                <span class="sb-layer-menu__caption">Color</span>
                <input
                  type="color"
                  aria-label="3D model tint color"
                  :disabled="!visualizationSettings.model.tintEnabled"
                  :value="visualizationSettings.model.color"
                  @input="setVisualizationSetting('model', 'color', $event.target.value)"
                />
              </label>
            </div>
            <div class="sb-layer-menu__row">
              <span class="sb-layer-menu__caption">Edges</span>
              <div class="sb-marker-scope" role="radiogroup" aria-label="3D rendering style">
                <button
                  type="button"
                  :class="{ on: visualizationSettings.model.renderStyle === 'shaded' }"
                  :aria-pressed="visualizationSettings.model.renderStyle === 'shaded'"
                  @click="setVisualizationSetting('model', 'renderStyle', 'shaded')"
                >Shaded</button>
                <button
                  type="button"
                  :class="{ on: visualizationSettings.model.renderStyle === 'wireframe' }"
                  :aria-pressed="visualizationSettings.model.renderStyle === 'wireframe'"
                  @click="setVisualizationSetting('model', 'renderStyle', 'wireframe')"
                >Outlined</button>
              </div>
            </div>
            <div class="sb-layer-menu__row">
              <label
                class="sb-layer-check"
                title="Show the labelled X / Y / Z triad at the support crop box"
              >
                <input
                  type="checkbox"
                  :checked="visualizationSettings.model.showAxes"
                  @change="setVisualizationSetting('model', 'showAxes', $event.target.checked)"
                />
                <span>Axes</span>
              </label>
            </div>
            <!-- Captions are prefixed "Axes" because this popover already has an
                 Opacity slider for the model itself; two rows both reading
                 "Opacity" would be ambiguous however they are grouped. -->
            <label class="sb-layer-slider">
              <span>
                <span class="sb-layer-menu__caption">Axes opacity</span>
                <strong>{{ Math.round(visualizationSettings.model.axesOpacity * 100) }}%</strong>
              </span>
              <input
                type="range"
                :min="axesOpacityRange.min"
                :max="axesOpacityRange.max"
                :step="axesOpacityRange.step"
                :disabled="!visualizationSettings.model.showAxes"
                aria-label="Axis triad opacity"
                :value="visualizationSettings.model.axesOpacity"
                @input="setVisualizationSetting('model', 'axesOpacity', $event.target.value)"
              />
            </label>
            <!-- Size commits on CHANGE, not input: it rebuilds the triad's
                 geometry, so riding @input would rebuild once per pixel of drag.
                 Opacity above is a material write and can ride @input safely. -->
            <label class="sb-layer-slider">
              <span>
                <span class="sb-layer-menu__caption">Axes size</span>
                <strong>{{ Number(visualizationSettings.model.axesSize).toFixed(1) }}×</strong>
              </span>
              <input
                type="range"
                :min="axesSizeRange.min"
                :max="axesSizeRange.max"
                :step="axesSizeRange.step"
                :disabled="!visualizationSettings.model.showAxes"
                aria-label="Axis triad size"
                :value="visualizationSettings.model.axesSize"
                @change="setVisualizationSetting('model', 'axesSize', $event.target.value)"
              />
            </label>
            <button
              type="button"
              class="sb-layer-reset"
              @click="resetVisualizationSource('model')"
            >Reset model appearance</button>
          </div>
        </v-menu>
        <v-menu
          v-model="scanAppearanceMenuOpen"
          top
          offset-y
          :nudge-bottom="-4"
          :close-on-content-click="false"
          transition="slide-y-reverse-transition"
          content-class="sb-marker-popover"
        >
          <template v-slot:activator="{ on, attrs }">
            <button
              type="button"
              v-bind="attrs"
              :class="{ on: layers.scan }"
              :aria-pressed="layers.scan ? 'true' : 'false'"
              :aria-expanded="scanAppearanceMenuOpen ? 'true' : 'false'"
              aria-label="Point cloud appearance controls"
              :disabled="!viewerReady || !selectedScan"
              title="Control point cloud appearance"
              v-on="on"
            >Scan</button>
          </template>
          <div class="sb-layer-menu" aria-label="Point cloud appearance controls">
            <div class="sb-layer-menu__heading">
              <span>
                <strong>Point cloud</strong>
                <small>Increase separation around the measured feature</small>
              </span>
              <label class="sb-marker-switch">
                <input
                  type="checkbox"
                  :checked="visualizationSettings.scan.visible"
                  @change="setVisualizationSetting('scan', 'visible', $event.target.checked)"
                />
                <span>{{ visualizationSettings.scan.visible ? 'Shown' : 'Hidden' }}</span>
              </label>
            </div>
            <label class="sb-layer-slider">
              <span>
                <span class="sb-layer-menu__caption">Opacity</span>
                <strong>{{ Math.round(visualizationSettings.scan.opacity * 100) }}%</strong>
              </span>
              <input
                type="range"
                min="0.05"
                max="1"
                step="0.05"
                aria-label="Point cloud opacity"
                :value="visualizationSettings.scan.opacity"
                @input="setVisualizationSetting('scan', 'opacity', $event.target.value)"
              />
            </label>
            <div class="sb-layer-menu__row sb-layer-menu__row--stack">
              <span class="sb-layer-menu__caption">Color</span>
              <div class="sb-marker-scope" role="radiogroup" aria-label="Point cloud color mode">
                <button
                  v-for="mode in scanColorModes"
                  :key="mode.value"
                  type="button"
                  :class="{ on: visualizationSettings.scan.colorMode === mode.value }"
                  :aria-pressed="visualizationSettings.scan.colorMode === mode.value"
                  @click="setVisualizationSetting('scan', 'colorMode', mode.value)"
                >{{ mode.label }}</button>
              </div>
            </div>
            <div
              v-if="visualizationSettings.scan.colorMode === 'solid'"
              class="sb-layer-menu__row"
            >
              <span class="sb-layer-menu__caption">Solid color</span>
              <input
                type="color"
                aria-label="Point cloud solid color"
                :value="visualizationSettings.scan.color"
                @input="setVisualizationSetting('scan', 'color', $event.target.value)"
              />
            </div>
            <label class="sb-layer-slider">
              <span>
                <span class="sb-layer-menu__caption">Point size</span>
                <strong>{{ Number(visualizationSettings.scan.pointSize).toFixed(1) }}</strong>
              </span>
              <input
                type="range"
                min="1"
                max="12"
                step="0.5"
                aria-label="Point cloud point size"
                :value="visualizationSettings.scan.pointSize"
                @input="setVisualizationSetting('scan', 'pointSize', $event.target.value)"
              />
            </label>
            <div class="sb-layer-menu__options">
              <label>
                <input
                  type="radio"
                  name="verification-point-shape"
                  value="square"
                  :checked="visualizationSettings.scan.pointShape === 'square'"
                  @change="setVisualizationSetting('scan', 'pointShape', 'square')"
                />
                Square points
              </label>
              <label>
                <input
                  type="radio"
                  name="verification-point-shape"
                  value="circle"
                  :checked="visualizationSettings.scan.pointShape === 'circle'"
                  @change="setVisualizationSetting('scan', 'pointShape', 'circle')"
                />
                Circle points
              </label>
              <label>
                <input
                  type="checkbox"
                  :checked="visualizationSettings.scan.edl"
                  @change="setVisualizationSetting('scan', 'edl', $event.target.checked)"
                />
                Eye-dome lighting
              </label>
            </div>
            <button
              type="button"
              class="sb-layer-reset"
              @click="resetVisualizationSource('scan')"
            >Reset scan appearance</button>
          </div>
        </v-menu>
        <button
          type="button"
          :class="{ on: cropControlsVisible }"
          :aria-pressed="cropControlsVisible"
          :disabled="!viewerReady"
          :title="viewerReady ? (cropControlsVisible ? 'Hide crop controls' : 'Show crop controls') : 'Waiting for viewer'"
          @click="toggleCropControls"
        >Crop</button>
        <v-menu
          v-model="markerMenuOpen"
          top
          offset-y
          :nudge-bottom="-4"
          :close-on-content-click="false"
          transition="slide-y-reverse-transition"
          content-class="sb-marker-popover"
        >
          <template v-slot:activator="{ on, attrs }">
            <button
              type="button"
              v-bind="attrs"
              :class="{ on: markerSettings.visible }"
              :aria-pressed="markerSettings.visible ? 'true' : 'false'"
              :aria-expanded="markerMenuOpen ? 'true' : 'false'"
              title="Control verification point markers"
              v-on="on"
            >Marks</button>
          </template>
          <div class="sb-marker-menu" aria-label="Verification marker controls">
            <div class="sb-marker-menu__heading">
              <span>
                <strong>Point markers</strong>
                <small>Design, measured and slice picks</small>
              </span>
              <label class="sb-marker-switch">
                <input
                  type="checkbox"
                  :checked="markerSettings.visible"
                  @change="setMarkerSetting('visible', $event.target.checked)"
                />
                <span>{{ markerSettings.visible ? 'Shown' : 'Hidden' }}</span>
              </label>
            </div>
            <div class="sb-marker-menu__options">
              <label>
                <input
                  type="checkbox"
                  :checked="markerSettings.showDesign"
                  @change="setMarkerSetting('showDesign', $event.target.checked)"
                />
                Design
              </label>
              <label>
                <input
                  type="checkbox"
                  :checked="markerSettings.showAsBuilt"
                  @change="setMarkerSetting('showAsBuilt', $event.target.checked)"
                />
                As-built
              </label>
              <label>
                <input
                  type="checkbox"
                  :checked="markerSettings.showLabels"
                  @change="setMarkerSetting('showLabels', $event.target.checked)"
                />
                Labels
              </label>
            </div>
            <div class="sb-marker-menu__row">
              <span class="sb-marker-menu__caption">Scope</span>
              <div class="sb-marker-scope" role="radiogroup" aria-label="Marker scope">
                <button
                  type="button"
                  :class="{ on: markerSettings.scope === 'all' }"
                  :aria-pressed="markerSettings.scope === 'all'"
                  @click="setMarkerSetting('scope', 'all')"
                >All points</button>
                <button
                  type="button"
                  :class="{ on: markerSettings.scope === 'current' }"
                  :aria-pressed="markerSettings.scope === 'current'"
                  @click="setMarkerSetting('scope', 'current')"
                >Current</button>
              </div>
            </div>
            <label class="sb-marker-opacity">
              <span>
                <span class="sb-marker-menu__caption">Opacity</span>
                <strong>{{ Math.round(markerSettings.opacity * 100) }}%</strong>
              </span>
              <input
                type="range"
                min="0.2"
                max="1"
                step="0.1"
                :value="markerSettings.opacity"
                @input="setMarkerSetting('opacity', $event.target.value)"
              />
            </label>
            <!-- Plain "Size" is unambiguous here: nothing else in the MARKS
                 popover is a size. (Contrast the 3D popover, where the axes
                 sliders had to be prefixed because the model owns an Opacity
                 row of its own.) Rides @input — marker sizing is a CSS variable
                 write, so live drag costs no rebuild. -->
            <label class="sb-marker-opacity">
              <span>
                <span class="sb-marker-menu__caption">Size</span>
                <strong>{{ Number(markerSettings.markerSize).toFixed(1) }}×</strong>
              </span>
              <input
                type="range"
                :min="markerSizeRange.min"
                :max="markerSizeRange.max"
                :step="markerSizeRange.step"
                aria-label="Point marker size"
                :value="markerSettings.markerSize"
                @input="setMarkerSetting('markerSize', $event.target.value)"
              />
            </label>
          </div>
        </v-menu>
      </div>
    </div>

    <!-- Cell 4 · Live screen scale + the too-coarse gate. A pick taken at a
         scale coarser than tolerance/3 is not a measurement (spike: 145 mm of
         scatter at 13.9 mm/px vs 4 mm at 0.4 mm/px), so the surveyor is told
         rather than left to trust a confident wrong number. Measured by
         SliceController and relayed here through WorkspacePanel. -->
    <div class="sb-cell sb-scale" :class="`sb-scale--${scale.state}`">
      <span class="cap">Scale</span>
      <span class="scale-value">{{ scale.value }}</span>
      <template v-if="scale.note">
        <span class="sep" aria-hidden="true">·</span>
        <span class="scale-note" :role="scale.coarse ? 'alert' : null">
          {{ scale.note }}
        </span>
      </template>
    </div>

    <!-- Cell 5 · Active scan — the point cloud every as-built reading is taken
         against. Named explicitly rather than left implicit: a silently-absent
         scan is exactly how "model-only mode" went unnoticed, and a silently
         WRONG scan would make every deviation on a conformance report wrong
         while still looking plausible. The surveyor must be able to see it. -->
    <div class="sb-cell sb-scan">
      <span class="cap">Scan</span>
      <v-menu
        v-model="scanMenuOpen"
        top
        offset-y
        :nudge-bottom="-4"
        :close-on-content-click="false"
        transition="slide-y-reverse-transition"
        content-class="sb-menu-popover"
        @input="onScanMenuToggle"
      >
        <template v-slot:activator="{ on, attrs }">
          <button
            type="button"
            class="sb-combo sb-scan-combo"
            v-bind="attrs"
            :aria-expanded="scanMenuOpen ? 'true' : 'false'"
            aria-haspopup="listbox"
            :disabled="!hasScanOptions"
            :title="scanNameTitle"
            v-on="on"
          >
            <span
              class="scan-name"
              :class="{ 'scan-name--none': !selectedScan }"
            >{{ scanNameLabel }}</span>
            <span v-if="scanIsManual" class="scan-flag" title="Chosen manually">MANUAL</span>
            <span class="chev" aria-hidden="true">
              <svg viewBox="0 0 10 10"><path d="M2 4l3 3 3-3" /></svg>
            </span>
          </button>
        </template>

        <div class="sb-menu sb-scan-menu" role="listbox" aria-label="Choose scan">
          <div class="scan-filter">
            <input
              ref="scanFilterInput"
              v-model="scanFilter"
              type="text"
              class="scan-filter-input"
              placeholder="Filter scans…"
              aria-label="Filter scans"
              @keydown.esc.stop="closeScanMenu"
            />
            <span class="scan-filter-count">{{ scanFilterCount }}</span>
          </div>

          <div class="sb-scan-scroll">
            <!-- Automatic — the derived suggestion, always offered so a manual
                 choice is reversible without a page reload. -->
            <button
              type="button"
              class="sb-menu-item"
              :class="{ on: !manualScanName }"
              role="option"
              :aria-selected="!manualScanName ? 'true' : 'false'"
              @click="chooseScan(null)"
            >
              <span class="mname">Automatic</span>
              <span class="msub">{{ autoScanLabel }}</span>
            </button>

            <div class="sb-menu-divider" aria-hidden="true"></div>

            <!-- Project scans first: 360° multi-side coverage, so deviation is
                 visible from any angle. -->
            <template v-if="filteredProjectScans.length">
              <div class="scan-group-cap">Project scans</div>
              <button
                v-for="scan in filteredProjectScans"
                :key="`p-${scan.name}`"
                type="button"
                class="sb-menu-item"
                :class="{ on: manualScanName === scan.name }"
                role="option"
                :aria-selected="manualScanName === scan.name ? 'true' : 'false'"
                :title="scanOptionTitle(scan)"
                @click="chooseScan(scan.name)"
              >
                <span class="mname">{{ scan.name }}</span>
                <span class="scan-tag scan-tag--project">PROJECT</span>
                <span v-if="scan.superseded" class="scan-tag scan-tag--old">OLD</span>
              </button>
            </template>

            <!-- Individual scans: single viewpoint, so the verified feature can
                 sit in an occlusion shadow. Listed second, never promoted. -->
            <template v-if="filteredIndividualScans.length">
              <div class="scan-group-cap">
                Individual scans
                <span class="scan-group-note">single setup</span>
              </div>
              <button
                v-for="scan in filteredIndividualScans"
                :key="`i-${scan.name}`"
                type="button"
                class="sb-menu-item"
                :class="{ on: manualScanName === scan.name }"
                role="option"
                :aria-selected="manualScanName === scan.name ? 'true' : 'false'"
                :title="scanOptionTitle(scan)"
                @click="chooseScan(scan.name)"
              >
                <span class="mname">{{ scan.name }}</span>
                <span v-if="scan.parent" class="msub">{{ scan.parent }}</span>
                <span v-if="scan.superseded" class="scan-tag scan-tag--old">OLD</span>
              </button>
            </template>

            <div v-if="!filteredProjectScans.length && !filteredIndividualScans.length"
                 class="scan-empty">No scan matches “{{ scanFilter }}”</div>
          </div>
        </div>
      </v-menu>
    </div>

    <!-- Cell 6 · Camera — Perspective / Orthographic -->
    <div class="sb-cell sb-camera">
      <span class="cap">Camera</span>
      <div class="seg" role="radiogroup" aria-label="Camera mode">
        <button
          type="button"
          :class="{ on: cameraMode === 'perspective' }"
          :aria-pressed="cameraMode === 'perspective'"
          :disabled="!viewerReady"
          title="Perspective"
          aria-label="Perspective camera"
          @click="setCameraMode('perspective')"
        >
          <svg viewBox="0 0 16 16" aria-hidden="true">
            <path d="M3 12L8 3L13 12L8 10Z" />
            <path d="M3 12L13 12" opacity=".5" />
          </svg>
        </button>
        <button
          type="button"
          :class="{ on: cameraMode === 'orthographic' }"
          :aria-pressed="cameraMode === 'orthographic'"
          :disabled="!viewerReady"
          title="Orthographic"
          aria-label="Orthographic camera"
          @click="setCameraMode('orthographic')"
        >
          <svg viewBox="0 0 16 16" aria-hidden="true">
            <rect x="3" y="3" width="10" height="10" />
            <path d="M3 8L13 8M8 3L8 13" opacity=".4" />
          </svg>
        </button>
      </div>
    </div>

    <!-- Cell 7 · Style — Shaded / Outlined -->
    <!-- The same model renderStyle is exposed in the 3D appearance popover. -->
    <div class="sb-cell sb-style">
      <span class="cap">Style</span>
      <div class="seg" role="radiogroup" aria-label="Visual style">
        <button
          type="button"
          :class="{ on: visualStyle === 'shaded' }"
          :aria-pressed="visualStyle === 'shaded'"
          :disabled="!viewerReady"
          title="Shaded"
          aria-label="Shaded rendering"
          @click="setVisualStyle('shaded')"
        >
          <svg viewBox="0 0 16 16" aria-hidden="true">
            <rect x="3" y="3" width="10" height="10" fill="currentColor" stroke="none" />
          </svg>
        </button>
        <button
          type="button"
          :class="{ on: visualStyle === 'wireframe' }"
          :aria-pressed="visualStyle === 'wireframe'"
          :disabled="!viewerReady"
          title="Outlined geometry"
          aria-label="Outlined rendering"
          @click="setVisualStyle('wireframe')"
        >
          <svg viewBox="0 0 16 16" aria-hidden="true">
            <path d="M3 5H13M3 8H13M3 11H13" />
          </svg>
        </button>
      </div>
    </div>

    <!-- Cell 8 · Fit — standalone action button (no cap — Cell 2 owns "VIEW"
         caption; adding one here would duplicate. The button's icon + text
         label is self-descriptive). -->
    <div class="sb-cell sb-fit">
      <button
        type="button"
        class="sb-single"
        :disabled="!viewerReady"
        :title="viewerReady ? 'Fit camera to verified tag extents' : 'Waiting for viewer'"
        aria-label="Fit to extents"
        @click="handleFit"
      >
        <svg viewBox="0 0 16 16" aria-hidden="true">
          <path d="M3 5V3H5M11 3H13V5M13 11V13H11M5 13H3V11" />
          <circle cx="8" cy="8" r="2.5" opacity=".6" />
        </svg>
        <span>Fit</span>
      </button>
    </div>
  </div>
</template>

<script>
/**
 * ViewerStatusBar — verification workspace viewer chrome.
 *
 * Eight cells (left-to-right):
 *   1. State + Hint       — LED + label + contextual hint / slice refusal
 *   2. View combo         — 6 world-fixed ortho views + Isometric + view lock
 *   3. Layers             — 3D / Scan / Crop / Marks toggles and popovers
 *   4. Scale              — live mm/px, and whether it can assess the tolerance
 *   5. Scan               — the point cloud as-built readings are taken against
 *   6. Camera             — Perspective / Orthographic (radio)
 *   7. Style              — Shaded / Outlined (radio — see the Style cell note)
 *   8. Fit                — Fit-to-extents single action (no cap)
 *
 * THIS IS THE VIEWER'S ONLY BOTTOM DOCK. SliceController used to own a second
 * 32 px bar directly above it carrying the plane tabs, a crop-size field, the
 * scale readout and the refusal notices. Two bars of chrome under one canvas
 * read as two applications, and the plane tabs duplicated the View combo here
 * exactly. So the tabs went (View drives the slice), the crop size went (the
 * box is a fixed 200 mm, resized with the crop handles), and the two surfaces
 * that carried real information — Cell 4 and the refusal text in Cell 1 —
 * moved here. Both arrive as props via WorkspacePanel; this component measures
 * neither, it only states them.
 *
 * Visual reference: Claude Design `design_handoff_viewer_status_bar/` (v2 —
 * adds the View combo that folded in the removed vertical ViewSwitcher rail).
 * Every Forge call routes through an API already used elsewhere in the
 * codebase — no new viewer plumbing beyond the orthographic view poses.
 *
 * State machine, layer visibility and appearance live in Vuex. Local data owns
 * only transient menu/camera-lock state. Active view id lives in Vuex too
 * (VERIFICATION/activeView) so hotkey handlers and both bottom bars stay in
 * sync even across route re-entry.
 */
import { mapGetters } from 'vuex';
import { log as debugLog, warn as debugWarn, error as debugError } from '@/utils/debugLogger';
import { computeViewPose } from '@/features/Verification/utils/computeViewPose';
import { verificationViewLabel } from '@/features/Verification/utils/verificationContext';
import {
  AXES_OPACITY_RANGE,
  AXES_SIZE_RANGE,
  MARKER_SIZE_RANGE,
} from '@/features/Verification/modules/verification.module';

/**
 * View schematic glyphs — one small SVG per view type, keyed by the `glyph`
 * field on `verificationContext.VERIFICATION_WORKSPACE_VIEWS[]`. Inline
 * attributes so they render correctly regardless of parent CSS; sizing comes
 * from .cg/.mg rules.
 *
 * The three `-rev` / `-up` variants are the opposite member of each pair. They
 * are not mirrors of their twin: the base shapes are near-symmetric, so a mirror
 * would render identically and the pair would be indistinguishable at 14 px.
 * Each reverse glyph instead flips the ONE asymmetric feature of its twin — the
 * ground line, the cut direction, the side the plate is read from.
 */
const VIEW_GLYPHS = Object.freeze({
  plan: '<svg viewBox="0 0 16 16" stroke="currentColor" fill="none" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"><rect x="3" y="3" width="10" height="10"/><path d="M3 6H13M3 9H13M3 12H13" opacity=".4"/></svg>',
  'plan-up': '<svg viewBox="0 0 16 16" stroke="currentColor" fill="none" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"><rect x="3" y="2" width="10" height="9"/><path d="M3 5H13M3 8H13" opacity=".4"/><path d="M6.4 15L8 13L9.6 15"/></svg>',
  elev: '<svg viewBox="0 0 16 16" stroke="currentColor" fill="none" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"><path d="M2 12h12M4 12l1-7h6l1 7"/><path d="M5 9h6" opacity=".5"/></svg>',
  'elev-rev': '<svg viewBox="0 0 16 16" stroke="currentColor" fill="none" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"><path d="M2 4h12M4 4l1 7h6l1-7"/><path d="M5 7h6" opacity=".5"/></svg>',
  sect: '<svg viewBox="0 0 16 16" stroke="currentColor" fill="none" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"><rect x="3" y="3" width="10" height="10"/><path d="M1 7L15 12" stroke-dasharray="2 2"/></svg>',
  'sect-rev': '<svg viewBox="0 0 16 16" stroke="currentColor" fill="none" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"><rect x="3" y="3" width="10" height="10"/><path d="M1 12L15 7" stroke-dasharray="2 2"/></svg>',
  iso: '<svg viewBox="0 0 16 16" stroke="currentColor" fill="none" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"><path d="M3 8L8 3L13 8L8 13Z"/><path d="M8 3L8 13M3 8L13 8" opacity=".4"/></svg>',
});

/**
 * status string → display descriptor. Keeps the template dumb.
 *   tone      — drives LED colour + label colour (ready/busy/error)
 *   label     — uppercase state name
 *   hint      — contextual prose
 *   loading   — shows the top-edge emerald sweep
 *   ledPulse  — applies `.active` class to pulse the LED (Phase 2 measuring)
 */
const STATUS_DISPLAY = Object.freeze({
  idle: {
    tone: 'busy',
    label: 'Idle',
    hint: 'Waiting for facility model',
    loading: false,
    ledPulse: false,
  },
  loading_model: {
    tone: 'busy',
    label: 'Loading',
    hint: 'Downloading · indexing scene',
    loading: true,
    ledPulse: false,
  },
  preparing: {
    tone: 'busy',
    label: 'Preparing',
    hint: 'Indexing geometry',
    loading: true,
    ledPulse: false,
  },
  loading_tags: {
    tone: 'busy',
    label: 'Loading',
    hint: 'Fetching verified tags',
    loading: true,
    ledPulse: false,
  },
  isolating: {
    tone: 'busy',
    label: 'Isolating',
    hint: 'Applying crop box + scan overlay',
    loading: true,
    ledPulse: false,
  },
  ready: {
    tone: 'ready',
    label: 'Ready',
    hint: 'Pick a check to begin measurement',
    loading: false,
    ledPulse: false,
  },
  error: {
    tone: 'error',
    label: 'Error',
    hint: '',
    loading: false,
    ledPulse: false,
  },
});

/**
 * `scale-change` states, as emitted by SliceController. Declared in
 * SliceController.vue and WorkspacePanel.vue too — the value travels through
 * both to get here. Keep the three copies in step.
 */
const SCALE_STATE = Object.freeze({
  UNKNOWN: 'unknown',
  NO_TOLERANCE: 'no-tolerance',
  ADEQUATE: 'adequate',
  COARSE: 'coarse',
});

export default {
  name: 'ViewerStatusBar',
  props: {
    /** Worst-axis screen scale in millimetres per pixel; null = not measured. */
    scaleMmPerPx: { type: Number, default: null },
    /** Tightest tolerance the current plane must assess, millimetres. */
    scaleToleranceMm: { type: Number, default: null },
    /** One of SCALE_STATE. The controller owns this; we do not re-derive it. */
    scaleState: { type: String, default: SCALE_STATE.UNKNOWN },
    /** Why the slice controller last refused to cut. '' when it did not. */
    sliceNotice: { type: String, default: '' },
  },
  data() {
    return {
      cameraMode: 'perspective',
      menuOpen: false,
      modelMenuOpen: false,
      scanAppearanceMenuOpen: false,
      markerMenuOpen: false,
      scanMenuOpen: false,
      scanFilter: '',
      scanColorModes: Object.freeze([
        { value: 'rgb', label: 'Original' },
        { value: 'solid', label: 'Solid' },
        { value: 'height', label: 'Height' },
      ]),
      /**
       * Orbit suppressed, pan + zoom kept. Local rather than Vuex: it is a
       * property of THIS viewer instance's navigation, dies with it, and no
       * other component reads it. Mirrored from the viewer on mount so a lock
       * left on by another route does not show as FREE.
       */
      viewLocked: false,
    };
  },
  computed: {
    ...mapGetters('VERIFICATION', {
      status: 'GET_VIEWER_STATUS',
      loadPercent: 'GET_VIEWER_LOAD_PERCENT',
      viewerError: 'GET_VIEWER_ERROR',
      selectedScan: 'GET_SELECTED_SCAN',
      autoScan: 'GET_AUTO_SCAN',
      scanOptions: 'GET_SCAN_OPTIONS',
      manualScanName: 'GET_MANUAL_SCAN_NAME',
      scanIsManual: 'GET_SCAN_IS_MANUAL',
      viewItems: 'GET_VIEW_ITEMS',
      activeView: 'GET_ACTIVE_VIEW',
      markerSettings: 'GET_MARKER_SETTINGS',
      visualizationSettings: 'GET_VISUALIZATION_SETTINGS',
    }),
    viewer() {
      return this.$store.state.viewer;
    },
    /** Slider bounds come from the module so they cannot drift from its clamps. */
    axesOpacityRange() {
      return AXES_OPACITY_RANGE;
    },
    axesSizeRange() {
      return AXES_SIZE_RANGE;
    },
    markerSizeRange() {
      return MARKER_SIZE_RANGE;
    },
    viewerReady() {
      return this.status === 'ready' || this.status === 'isolating';
    },
    layers() {
      return {
        model3d: this.visualizationSettings.model.visible,
        scan: this.visualizationSettings.scan.visible,
      };
    },
    visualStyle() {
      return this.visualizationSettings.model.renderStyle;
    },
    /**
     * Mirror of the global `isCropBoxVisible` Vuex state — updated by the
     * Cropping3DExtension when visibility changes (via toggleCropbox /
     * updateBoxVisibility). Our preflight hides controls on entry; user
     * clicks the Crop toggle to reveal them when they need to resize the
     * box manually.
     */
    viewLockTitle() {
      if (!this.viewerReady) return 'Waiting for viewer';
      return this.viewLocked
        ? 'View locked — pan and zoom only. Click to allow orbit.'
        : 'Lock the view: pan and zoom stay available, orbit is suppressed';
    },

    cropControlsVisible() {
      return !!this.$store.getters.getIsCropBoxVisible;
    },
    /** Name of the scan as-built readings are measured against. */
    scanNameLabel() {
      return this.selectedScan?.name || 'None';
    },
    scanNameTitle() {
      if (!this.selectedScan?.name) {
        return 'No scan resolved for this tag — click to choose one';
      }
      const how = this.scanIsManual ? 'chosen manually' : 'selected automatically';
      return `As-built readings are taken against scan "${this.selectedScan.name}" (${how}). Click to change.`;
    },
    /** What the derived cascade suggests, shown against the "Automatic" option. */
    autoScanLabel() {
      return this.autoScan?.name || 'no match';
    },
    hasScanOptions() {
      return (
        this.scanOptions.projectScans.length > 0 || this.scanOptions.individualScans.length > 0
      );
    },
    filteredProjectScans() {
      return this.filterScans(this.scanOptions.projectScans);
    },
    filteredIndividualScans() {
      return this.filterScans(this.scanOptions.individualScans);
    },
    /**
     * "n of m" so a filtered list never reads as the complete catalogue — with
     * 140+ scans it is otherwise impossible to tell a narrow filter from a
     * facility that genuinely has few captures.
     */
    scanFilterCount() {
      const shown = this.filteredProjectScans.length + this.filteredIndividualScans.length;
      const total =
        this.scanOptions.projectScans.length + this.scanOptions.individualScans.length;
      return shown === total ? `${total}` : `${shown} of ${total}`;
    },
    /**
     * Three things compete for the one hint slot, so the priority is explicit:
     * viewer error > slice refusal > the stock prompt. The refusal outranks the
     * prompt because it is the only one of the two the surveyor can act on, and
     * because an empty viewport with no explanation is how a frame mismatch
     * gets misread as "the slice feature is broken". A viewer that never loaded
     * outranks it in turn — it already explains the refusal, and stating both
     * would need two lines in a 34 px bar.
     */
    display() {
      const base = STATUS_DISPLAY[this.status] || STATUS_DISPLAY.idle;
      let hint = base.hint;
      let hintWarn = false;
      if (this.status === 'loading_model' && this.loadPercent > 0) {
        const pct = Math.round(this.loadPercent);
        hint = hint ? `${hint} (${pct}%)` : `${pct}%`;
      }
      if (this.sliceNotice) {
        hint = this.sliceNotice;
        hintWarn = true;
      }
      if (this.status === 'error') {
        hint = this.viewerError || 'Viewer failed to load';
        hintWarn = false;
      }
      return { ...base, hint, hintWarn };
    },

    /**
     * The live screen scale, and whether this view can honestly assess the
     * active tolerance. Three distinct states, deliberately not two: "not
     * measured yet" must not render as "too coarse", or the bar cries wolf on a
     * conformance tool. `scaleState` is the controller's verdict and is taken as
     * given; only the wording is decided here.
     */
    scale() {
      const mm = this.scaleMmPerPx;
      const state = this.scaleState;
      if (state === SCALE_STATE.UNKNOWN || !Number.isFinite(mm)) {
        return { state: SCALE_STATE.UNKNOWN, value: '—', note: '', coarse: false };
      }
      // Below 1 mm/px the second decimal is the whole signal; above it, noise.
      const value = `${mm < 1 ? mm.toFixed(2) : mm.toFixed(1)} mm/px`;
      const tolerance = this.scaleToleranceMm;
      const coarse = state === SCALE_STATE.COARSE;
      if (!tolerance) return { state, value, note: 'no tolerance set', coarse };
      if (coarse) {
        return {
          state,
          value,
          note: `view too coarse to assess ±${tolerance} mm — zoom in`,
          coarse,
        };
      }
      return { state, value, note: `ok for ±${tolerance} mm`, coarse };
    },
    /** Resolved view item for the current activeView id. */
    activeViewData() {
      return this.viewItems.find((v) => v.id === this.activeView) || null;
    },
    /**
     * Hotkey → view id, DERIVED from the same rows the menu renders.
     *
     * This used to be a frozen literal beside the view table, i.e. a second
     * source of truth for `kbd`. Adding a view without touching it produced a
     * row with a key badge that did nothing, which reads as a broken keyboard
     * rather than as missing wiring. Now a key exists exactly when a row
     * declares it.
     */
    hotkeyToView() {
      return this.viewItems.reduce((byKey, item) => {
        if (item?.kbd) byKey[item.kbd] = item.id;
        return byKey;
      }, {});
    },
    /** Inline SVG string for the currently-active view — rendered via v-html. */
    activeViewGlyph() {
      return this.activeViewData ? VIEW_GLYPHS[this.activeViewData.glyph] || '' : '';
    },
  },
  watch: {
    viewerReady: {
      immediate: true,
      handler(ready) {
        if (!ready) return;
        const cam = this.viewer?.navigation?.getCamera?.();
        if (cam && typeof cam.isPerspective === 'boolean') {
          this.cameraMode = cam.isPerspective ? 'perspective' : 'orthographic';
        }
      },
    },
  },
  mounted() {
    // Hotkey listener at window level; guarded against input typing inside
    // the Context / Results panels so the numbers don't hijack text fields.
    this._onKeydownBound = this.handleKeydown.bind(this);
    window.addEventListener('keydown', this._onKeydownBound);
    // Mirror whatever the viewer already has, so a lock left engaged elsewhere
    // does not render here as FREE while orbit is actually dead.
    try {
      this.viewLocked = !!this.viewer?.navigation?.getIsLocked?.();
    } catch (err) {
      debugWarn('[ViewerStatusBar] could not read initial navigation lock', err);
    }
  },
  beforeDestroy() {
    window.removeEventListener('keydown', this._onKeydownBound);
    this._onKeydownBound = null;
    // Never leave the next route with orbit suppressed. The lock lives on the
    // viewer, which outlives this component, so releasing it is our job.
    if (this.viewLocked) {
      try {
        this.viewer?.setNavigationLockSettings?.({
          orbit: true, pan: true, zoom: true, roll: true, fov: true, gotoview: true, walk: true,
        });
        this.viewer?.setNavigationLock?.(false);
        debugLog('[ViewerStatusBar] view lock released on teardown');
      } catch (err) {
        debugError('[ViewerStatusBar] releasing view lock on teardown failed', err);
      }
    }
  },
  methods: {
    setMarkerSetting(key, value) {
      this.$store.commit('VERIFICATION/SET_MARKER_SETTING', { key, value });
    },
    setVisualizationSetting(source, key, value) {
      this.$store.commit('VERIFICATION/SET_VISUALIZATION_SETTING', {
        source, key, value,
      });
    },
    resetVisualizationSource(source) {
      this.$store.commit('VERIFICATION/RESET_VISUALIZATION_SOURCE', source);
    },
    getGlyph(glyphKey) {
      return VIEW_GLYPHS[glyphKey] || '';
    },
    /**
     * "Elevation · Top". Composed by the view table's own helper so the combo
     * button, its tooltip and the menu row cannot drift apart.
     */
    viewLabel(item) {
      return verificationViewLabel(item);
    },
    /**
     * Layout helper: insert a visual divider after the last ortho view.
     *
     * Works for any number of ortho rows, but it DEPENDS on `iso` being the
     * last entry of `VERIFICATION_WORKSPACE_VIEWS` — it fires on the single
     * non-iso -> iso transition. Put `iso` in the middle and the divider lands
     * mid-list with nothing enforcing otherwise.
     */
    isOrthoDividerAfter(idx) {
      const current = this.viewItems[idx];
      const next = this.viewItems[idx + 1];
      // Divider fires when crossing from non-iso to iso.
      return !!(current && next && current.id !== 'iso' && next.id === 'iso');
    },
    /**
     * Toggle the Cropping3DExtension's visual chrome (mesh outline + drag
     * arrows). The crop clipping effect stays active — this only flips the
     * on-screen handles. Wired to `updateBoxVisibility` which internally
     * commits `isCropBoxVisible` to the root Vuex store, so our computed
     * `cropControlsVisible` tracks the live state without us owning it.
     *
     * Requires `croppingOn=true` on the extension — our preflight sets that
     * via toggleCropbox(true), so this call is a no-op only if preflight
     * hasn't run (guarded by the button's `!viewerReady` disable).
     */
    /**
     * Suppress orbit while keeping pan, zoom and view changes.
     *
     * `setNavigationLockSettings` is a WHITELIST of what survives the lock —
     * verified live: with `{orbit:false, pan:true, zoom:true, gotoview:true}`
     * and the lock engaged, `navigation.isActionEnabled` reports orbit false and
     * the other three true. `gotoview` matters: without it the View combo beside
     * this button would silently stop working while locked.
     *
     * The settings are restored to all-true on release so nothing downstream
     * inherits a half-locked navigation.
     */
    toggleViewLock() {
      const viewer = this.viewer;
      if (!viewer || typeof viewer.setNavigationLock !== 'function') {
        debugError('[ViewerStatusBar] navigation lock unavailable');
        return;
      }
      // Invert the VIEWER's actual state, not our remembered one. The lock is a
      // single global flag that other code can clear (Cropping3DExtension used
      // to, on every click), so a component-local boolean can drift out of sync
      // and then toggle the wrong way — observed live before that was fixed.
      // Reading the truth first makes the button self-correcting.
      const actual = !!this.viewer?.navigation?.getIsLocked?.();
      if (actual !== this.viewLocked) {
        debugWarn('[ViewerStatusBar] view-lock state had drifted; resyncing from the viewer', {
          remembered: this.viewLocked, actual,
        });
        this.viewLocked = actual;
      }
      const next = !actual;
      try {
        viewer.setNavigationLockSettings({
          orbit: !next, pan: true, zoom: true, roll: !next, fov: !next, gotoview: true, walk: !next,
        });
        viewer.setNavigationLock(next);
        this.viewLocked = next;
        debugLog('[ViewerStatusBar] view lock', {
          locked: next,
          orbit: viewer.navigation?.isActionEnabled?.('orbit'),
          pan: viewer.navigation?.isActionEnabled?.('pan'),
          zoom: viewer.navigation?.isActionEnabled?.('zoom'),
        });
      } catch (err) {
        debugError('[ViewerStatusBar] view lock failed', err);
      }
    },

    toggleCropControls() {
      const viewer = this.viewer;
      if (!viewer) return;
      const cropExt = viewer.getExtension?.('Cropping3DExtension');
      if (!cropExt || typeof cropExt.updateBoxVisibility !== 'function') {
        debugError('[ViewerStatusBar] Cropping3DExtension.updateBoxVisibility unavailable');
        return;
      }
      const next = !this.cropControlsVisible;
      debugLog('[ViewerStatusBar] toggle crop controls', { visible: next });
      try {
        cropExt.updateBoxVisibility(next);
      } catch (err) {
        debugError('[ViewerStatusBar] updateBoxVisibility failed', err);
      }
    },
    /** viewer.navigation.toPerspective/toOrthographic — pattern from PointCloudEraserExtension. */
    setCameraMode(mode) {
      const nav = this.viewer?.navigation;
      if (!nav) return;
      if (mode === this.cameraMode) return;
      debugLog('[ViewerStatusBar] camera mode', mode);
      try {
        if (mode === 'orthographic') nav.toOrthographic();
        else nav.toPerspective();
        this.cameraMode = mode;
      } catch (err) {
        debugError('[ViewerStatusBar] setCameraMode failed', err);
      }
    },
    /** Style is part of the shared model-appearance contract. */
    setVisualStyle(style) {
      if (style === this.visualStyle) return;
      if (!['shaded', 'wireframe'].includes(style)) return;

      debugLog('[ViewerStatusBar] visual style', { from: this.visualStyle, to: style });
      this.setVisualizationSetting('model', 'renderStyle', style);
    },
    /** Fit to the verified-tag crop region; fall back to viewer.fitToView(). */
    handleFit() {
      const viewer = this.viewer;
      if (!viewer) return;
      const cropBox = this.$store.getters['VERIFICATION/GET_VERIFIED_CROP_BOX'];
      debugLog('[ViewerStatusBar] fit', { hasCropBox: !!cropBox });
      try {
        if (cropBox && viewer.navigation?.fitBounds) {
          viewer.navigation.fitBounds(false, cropBox);
        } else if (typeof viewer.fitToView === 'function') {
          viewer.fitToView();
        }
      } catch (err) {
        debugError('[ViewerStatusBar] fit failed', err);
      }
    },
    /** Case-insensitive substring match on the scan name. */
    filterScans(scans) {
      const needle = this.scanFilter.trim().toLowerCase();
      if (!needle) return scans;
      return scans.filter((s) => s.name.toLowerCase().includes(needle));
    },
    scanOptionTitle(scan) {
      const parts = [scan.name];
      if (scan.parent) parts.push(`part of ${scan.parent}`);
      if (scan.createDate) parts.push(`uploaded ${scan.createDate.slice(0, 10)}`);
      if (scan.superseded) parts.push('superseded revision');
      return parts.join(' · ');
    },
    /**
     * Commit the surveyor's choice. `null` reverts to the automatic pick.
     *
     * No viewer work here: VerificationViewer watches GET_SELECTED_SCAN and its
     * `applyScanOverlay` already takes the previous overlay down before showing
     * the new one, so switching is handled by the existing path.
     */
    chooseScan(scanName) {
      debugLog('[ViewerStatusBar] scan chosen', {
        scanName: scanName || '(automatic)',
        previous: this.manualScanName || '(automatic)',
        autoWouldPick: this.autoScan?.name || null,
      });
      this.$store.commit('VERIFICATION/SET_MANUAL_SCAN', scanName);
      this.closeScanMenu();
    },
    closeScanMenu() {
      this.scanMenuOpen = false;
    },
    /**
     * Reset the filter each time the menu opens and focus the input.
     * A filter left over from last time would silently hide most of the
     * catalogue behind a box the user has to notice and clear.
     */
    onScanMenuToggle(open) {
      if (!open) return;
      this.scanFilter = '';
      this.$nextTick(() => this.$refs.scanFilterInput?.focus());
    },
    requestView(viewId) {
      if (!viewId) return;
      this.$emit('view-change', viewId);
    },
    /**
     * Apply a camera pose after WorkspacePanel has reconciled the canonical
     * Slice/View state. Kept public for Slice 3D -> Isometric synchronization.
     *
     * The six ortho views are posed from WORLD axes, so the view a row declares
     * is the plane actually on screen. Only Isometric still derives its pose
     * from the verified tag's local frame — see `computeViewPose` for why that
     * split exists and what a bounding-box-derived ortho frame silently broke.
     */
    applyView(viewId, { commit = true } = {}) {
      if (!viewId) return;
      const viewer = this.viewer;
      const supportCrop = this.$store.getters['VERIFICATION/GET_VERIFIED_CROP_BOX'];
      const cropExt = viewer?.getExtension?.('Cropping3DExtension');
      const activeCrop = cropExt?.cropBox || supportCrop;
      if (!viewer?.navigation || !supportCrop || !activeCrop) {
        debugLog('[ViewerStatusBar] setView skipped — no viewer or crop box', { viewId });
        return;
      }

      debugLog('[ViewerStatusBar] setView', viewId);
      if (commit) this.$store.commit('VERIFICATION/SET_ACTIVE_VIEW', viewId);

      // `supportCrop` supplies the framing DISTANCE (stable — the live crop
      // would make the camera jitter as the handles are dragged) and Iso's local
      // frame; target and fit use whichever crop SliceController made live. The
      // six ortho views keep the edited measurement crop; Isometric
      // intentionally receives the restored support crop.
      const pose = computeViewPose(viewId, supportCrop, activeCrop);
      if (!pose) {
        debugLog('[ViewerStatusBar] setView: unknown viewId', viewId);
        return;
      }

      try {
        viewer.navigation.setView(pose.position, pose.target, pose.upVector);
        viewer.navigation?.fitBounds?.(false, activeCrop);
      } catch (err) {
        debugError('[ViewerStatusBar] setView call failed', err);
      }
    },
    // View-pose math lives in utils/computeViewPose.js — same helpers are
    // consumed by VerificationViewer during preflight (default Iso landing).
    /**
     * Global hotkey handler — the numeric keys declared by the view rows
     * (1-6 / 0 today) switch views without opening the menu. Suppressed inside
     * text inputs so typing isn't hijacked.
     */
    handleKeydown(e) {
      if (!this.viewerReady) return;
      const target = e.target;
      if (target && typeof target.matches === 'function' &&
          target.matches('input, textarea, [contenteditable="true"]')) {
        return;
      }
      if (e.ctrlKey || e.metaKey || e.altKey || e.shiftKey) return;
      const viewId = this.hotkeyToView[e.key];
      if (!viewId) return;
      e.preventDefault();
      this.requestView(viewId);
    },
  },
};
</script>

<style lang="scss" scoped>
@import '../../styles/verification-tokens';

/* ============ Viewer Status Bar ============
 * Visual spec: Claude Design `design_handoff_viewer_status_bar/` (v2 — adds
 * the View combo as Cell 2). Active-state treatment (emerald ink + subtle
 * tint + 1.5px underline bar) reads cleaner than a filled pill, especially
 * for the independent-layers case where both segments may be active.
 */

.statusbar {
  position: absolute;
  left: 0;
  right: 0;
  bottom: 0;
  z-index: 5;
  height: 34px;
  display: flex;
  align-items: stretch;
  background: $vf-sb-bg;
  border-top: 1px solid $vf-sb-border-top;
  font-family: $vf-font-mono;
  font-size: 11px;
  color: $vf-sb-ink-2;
  letter-spacing: 0.04em;
  overflow: hidden;
  user-select: none;
}

.sb-cell {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 0 12px;
  border-right: 1px solid $vf-sb-sep;
  white-space: nowrap;
  flex-shrink: 0;

  &:last-child {
    border-right: 0;
  }
}

.cap {
  font-size: 9px;
  font-weight: 500;
  color: $vf-sb-coord-sep;
  text-transform: uppercase;
  letter-spacing: 0.1em;
}

/* ---- Cell 4 · Screen scale ---- */
.sb-scale {
  min-width: 0;

  .scale-value {
    color: $vf-sb-ink-1;
    font-weight: 600;
  }

  .sep {
    color: $vf-sb-coord-sep;
    flex-shrink: 0;
  }

  .scale-note {
    overflow: hidden;
    text-overflow: ellipsis;
    font-size: 10px;
  }

  /* Amber, matching the "awaiting" LED. The warning is non-blocking — the
   * surveyor may still pick; they just do it knowing the view cannot support
   * the tolerance. */
  &--coarse {
    .scale-value,
    .scale-note {
      color: $vf-sb-led-busy;
    }
  }

  &--adequate .scale-value {
    color: $vf-sb-led-ready;
  }
}

/* ---- Cell 5 · Active scan ---- */
.sb-scan {
  min-width: 0;

  .scan-name {
    max-width: 200px;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    color: $vf-sb-ink-1;
  }

  // Amber, matching the "awaiting" LED — an unresolved scan means as-built
  // capture is blocked, which is a state worth noticing, not a neutral default.
  .scan-name--none {
    color: $vf-sb-led-busy;
  }

  // The scan name is the payload here, so it carries the emphasis the VIEW
  // combo gives its `.vlabel`.
  .sb-scan-combo .scan-name {
    font-size: 11px;
    font-weight: 600;
    letter-spacing: 0.02em;
    text-transform: none;
  }

  // Marks a scan the surveyor chose over the derived suggestion. Deliberately
  // quiet — it is provenance, not an alert.
  .scan-flag {
    font-size: 8.5px;
    letter-spacing: 0.1em;
    padding: 1px 4px;
    border: 1px solid $vf-sb-kbd-border;
    border-radius: 2px;
    color: $vf-sb-ink-2;
  }
}

/* ---- Cell 1 · State + Hint ---- */
.sb-state {
  flex: 1;
  min-width: 0;
  position: relative;
  overflow: hidden;
  gap: 10px;

  .led {
    width: 8px;
    height: 8px;
    border-radius: 999px;
    background: $vf-sb-led-busy;
    box-shadow: 0 0 6px $vf-sb-led-busy;
    flex-shrink: 0;

    &--ready {
      background: $vf-sb-led-ready;
      box-shadow: 0 0 6px $vf-sb-led-ready;
    }
    &--busy {
      background: $vf-sb-led-busy;
      box-shadow: 0 0 6px $vf-sb-led-busy;
    }
    &--error {
      background: $vf-sb-led-error;
      box-shadow: 0 0 6px $vf-sb-led-error;
    }
  }

  .label {
    font-weight: 600;
    letter-spacing: 0.08em;
    font-size: 10px;
    flex-shrink: 0;

    &--ready { color: $vf-sb-led-ready; }
    &--busy  { color: $vf-sb-led-busy; }
    &--error { color: $vf-sb-led-error; }
  }

  .sep {
    color: $vf-sb-coord-sep;
    flex-shrink: 0;
  }

  .hint {
    color: $vf-sb-ink-2;
    overflow: hidden;
    text-overflow: ellipsis;
    min-width: 0;
    flex: 1;
  }

  /* Amber, not rose: a slice refusal blocks capture but nothing has failed —
     the same reading `.scan-name--none` gets for an unresolved scan. Rose
     sitting beside a green READY LED would contradict the LED. */
  .hint--warn {
    color: $vf-sb-led-busy;
  }

  &.active .led {
    animation: sb-pulse 1.6s ease-in-out infinite;
  }
}

@keyframes sb-pulse {
  0%, 100% { opacity: 1; }
  50%      { opacity: 0.4; }
}

.sb-loading {
  position: absolute;
  left: 0;
  right: 0;
  top: 0;
  height: 2px;
  background: linear-gradient(90deg, transparent, $vf-sb-led-ready, transparent);
  background-size: 30% 100%;
  background-repeat: no-repeat;
  animation: sb-loading-sweep 1.2s linear infinite;
  pointer-events: none;
}

@keyframes sb-loading-sweep {
  0%   { background-position: -30% 0; }
  100% { background-position: 130% 0; }
}

/* ---- Cell 2 · View combo trigger ----
 * Dropdown content lives in <v-menu> which Vuetify teleports to body; its
 * styles are in the non-scoped block below. */
.sb-view-cell {
  position: relative;
}

/* View lock. Sits with the View combo because it constrains the view, not the
   camera hardware — the Camera cell is projection (perspective/ortho). */
.sb-lock {
  display: inline-flex;
  align-items: center;
  gap: 5px;
  height: 20px;
  padding: 0 7px;
  border: 1px solid $vf-sb-btn-border;
  border-radius: 3px;
  background: $vf-sb-btn-bg;
  color: $vf-sb-ink-2;
  font-family: $vf-font-mono;
  font-size: 9px;
  letter-spacing: 0.08em;
  cursor: pointer;
  transition: background 0.12s ease, border-color 0.12s ease, color 0.12s ease;

  svg {
    width: 11px;
    height: 11px;
    fill: none;
    stroke: currentColor;
    stroke-width: 1.1;
    stroke-linecap: round;
  }

  &:hover:not(:disabled) {
    background: $vf-sb-btn-hover-bg;
    border-color: $vf-sb-btn-hover-bd;
    color: $vf-sb-ink-1;
  }

  /* State is carried by the padlock glyph AND the word, never by colour alone —
     the same rule the rest of this feature follows for PASS/FAIL. */
  &.on {
    background: $vf-sb-snap-on-bg;
    border-color: $vf-sb-snap-on-bd;
    color: $vf-sb-led-ready;
  }

  &:disabled {
    opacity: 0.45;
    cursor: default;
  }
}

.sb-combo {
  appearance: none;
  height: 24px;
  padding: 0 8px 0 7px;
  border: 1px solid $vf-sb-btn-border;
  border-radius: 3px;
  background: $vf-sb-cmd-bg;
  color: $vf-sb-ink-1;
  font-family: $vf-font-mono;
  font-size: 10px;
  letter-spacing: 0.06em;
  cursor: pointer;
  transition: color 0.15s ease, background 0.15s ease, border-color 0.15s ease;
  display: inline-flex;
  align-items: center;
  gap: 7px;

  &:hover:not(:disabled),
  &[aria-expanded='true'] {
    color: $vf-sb-ink-1;
    border-color: $vf-sb-btn-hover-bd;
    background: $vf-sb-snap-on-bg;
  }

  &:disabled {
    opacity: 0.45;
    cursor: not-allowed;
  }

  .cg {
    display: inline-flex;
    color: $vf-sb-led-ready;

    ::v-deep svg {
      width: 13px;
      height: 13px;
    }
  }

  .vlabel {
    color: $vf-sb-ink-1;
    font-weight: 600;
    letter-spacing: 0.02em;
    text-transform: none;
    font-size: 11px;
  }

  .vsub {
    color: $vf-sb-ink-3;
    font-size: 9.5px;
    letter-spacing: 0.1em;
    text-transform: uppercase;
  }

  .chev {
    display: inline-flex;
    color: $vf-sb-ink-2;

    svg {
      width: 9px;
      height: 9px;
      stroke: currentColor;
      fill: none;
      stroke-width: 1.6;
      stroke-linecap: round;
      stroke-linejoin: round;
    }
  }
}

/* ---- Cells 3 · 6 · 7 — Segmented control pattern ---- */
.seg {
  display: inline-flex;
  align-items: center;
  border: 1px solid $vf-sb-btn-border;
  border-radius: 3px;
  background: $vf-sb-cmd-bg;
  overflow: hidden;

  button {
    position: relative;
    height: 24px;
    min-width: 34px;
    padding: 4px 9px;
    border: 0;
    background: transparent;
    color: $vf-sb-ink-4;
    font-family: $vf-font-mono;
    font-size: 10px;
    font-weight: 500;
    letter-spacing: 0.08em;
    text-transform: uppercase;
    cursor: pointer;
    transition: color 0.15s ease, background 0.15s ease;
    display: inline-flex;
    align-items: center;
    justify-content: center;

    svg {
      width: 13px;
      height: 13px;
      stroke: currentColor;
      fill: none;
      stroke-width: 1.6;
      stroke-linecap: round;
      stroke-linejoin: round;
    }

    &:hover:not(:disabled) {
      color: $vf-sb-ink-1;
      background: $vf-sb-btn-hover-bg;
    }

    &:disabled {
      opacity: 0.45;
      cursor: not-allowed;
    }

    &.on {
      color: $vf-sb-led-ready;
      background: $vf-sb-snap-on-bg;

      &::after {
        content: '';
        position: absolute;
        left: 6px;
        right: 6px;
        bottom: 2px;
        height: 1.5px;
        background: $vf-sb-led-ready;
        border-radius: 1px;
      }
    }
  }

  button + button {
    border-left: 1px solid $vf-sb-sep;
  }
}

/* ---- Cell 8 · Fit standalone button ---- */
.sb-single {
  height: 24px;
  padding: 0 10px;
  border: 1px solid $vf-sb-btn-border;
  border-radius: 3px;
  background: $vf-sb-cmd-bg;
  color: $vf-sb-ink-1;
  font-family: $vf-font-mono;
  font-size: 10px;
  font-weight: 500;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  cursor: pointer;
  transition: color 0.15s ease, background 0.15s ease, border-color 0.15s ease;
  display: inline-flex;
  align-items: center;
  gap: 6px;

  svg {
    width: 13px;
    height: 13px;
    stroke: currentColor;
    fill: none;
    stroke-width: 1.6;
    stroke-linecap: round;
    stroke-linejoin: round;
  }

  &:hover:not(:disabled) {
    color: $vf-sb-ink-1;
    border-color: $vf-sb-btn-hover-bd;
    background: $vf-sb-snap-on-bg;
  }

  &:disabled {
    opacity: 0.45;
    cursor: not-allowed;
  }
}
</style>

<style lang="scss">
/* v-menu teleports its content to <body> (outside the scoped tree), so the
 * popover must use non-scoped styles keyed on a unique content-class. */
@import '../../styles/verification-tokens';

.sb-menu-popover {
  // Raised one step off the bar with a soft shadow rather than a darker fill —
  // the popover is the same surface family as the chrome it opens from, so
  // elevation carries the separation, not hue.
  box-shadow: 0 12px 28px rgba(0, 0, 0, 0.22) !important;
  border-radius: 6px !important;
  overflow: visible !important;
  background: transparent !important;

  .sb-menu {
    min-width: 220px;
    padding: 4px;
    background: $vf-panel-2;
    border: 1px solid rgba(0, 0, 0, 0.14);
    border-radius: 6px;
  }

  /* Six ortho rows + a divider + Isometric is roughly twice the height the
     four-view menu had, opening UPWARD off a 34 px bar. On a laptop it fits;
     on a short viewport the top rows would be clipped by the window with no
     way to reach them, because the popover itself is `overflow: visible`.
     Scroll the list rather than the popover — same split the scan menu uses
     (`.sb-scan-scroll`), and it leaves the popover's shadow/radius alone. */
  .sb-menu.sb-view-menu {
    max-height: 56vh;
    overflow-y: auto;
    overscroll-behavior: contain;

    &::-webkit-scrollbar { width: 8px; }
    &::-webkit-scrollbar-thumb {
      background: rgba(0, 0, 0, 0.22);
      border-radius: 4px;
    }
    &::-webkit-scrollbar-track { background: transparent; }
  }

  .sb-menu-item {
    width: 100%;
    appearance: none;
    border: 0;
    background: transparent;
    display: grid;
    grid-template-columns: 22px 1fr auto auto;
    gap: 8px;
    align-items: center;
    padding: 6px 8px;
    border-radius: 3px;
    color: $vf-sb-ink-1;
    font-family: $vf-font-mono;
    cursor: pointer;
    text-align: left;
    transition: background 0.12s ease, color 0.12s ease;

    .mg {
      display: inline-flex;
      align-items: center;
      justify-content: center;
      color: $vf-sb-ink-2;

      svg {
        width: 14px;
        height: 14px;
      }
    }

    .mname {
      font-family: $vf-font-body;
      font-size: 11.5px;
      font-weight: 600;
      letter-spacing: 0.02em;
      text-transform: none;
      color: $vf-sb-ink-1;
    }

    .msub {
      font-size: 9.5px;
      text-transform: uppercase;
      letter-spacing: 0.08em;
      color: $vf-sb-ink-3;
    }

    .mkbd {
      font-size: 9px;
      padding: 1px 4px;
      border: 1px solid $vf-sb-kbd-border;
      border-radius: 2px;
      color: $vf-sb-ink-2;
      font-weight: 400;
    }

    &:hover {
      background: $vf-sb-btn-hover-bg;
      color: $vf-sb-ink-1;
    }

    &.on {
      background: $vf-sb-snap-on-bg;
      color: $vf-sb-ink-1;

      .mg      { color: $vf-sb-led-ready; }
      .msub    { color: $vf-sb-led-ready; }
    }
  }

  .sb-menu-divider {
    height: 1px;
    margin: 4px 2px;
    background: $vf-sb-sep;
  }

  /* ---- Scan selector ----
     Raised a full step off the status-bar chrome. 140+ scans with near-identical
     names (`1HDC0607ZL-001` vs `-002`) is a reading task, not a glance, so this
     panel trades the bar's low-contrast recede for legibility. */
  .sb-menu.sb-scan-menu {
    min-width: 330px;
    max-width: 380px;
    padding: 0;
    background: $vf-panel-2;
    border-color: rgba(0, 0, 0, 0.18);
    color: $vf-sb-ink-1;
  }

  .sb-scan-menu {
    .scan-filter {
      display: flex;
      align-items: center;
      gap: 8px;
      padding: 7px 9px;
      border-bottom: 1px solid rgba(0, 0, 0, 0.1);
    }

    .scan-filter-input {
      flex: 1 1 auto;
      min-width: 0;
      height: 22px;
      border: 1px solid rgba(0, 0, 0, 0.16);
      border-radius: 3px;
      background: $vf-sb-btn-bg;
      color: $vf-sb-ink-1;
      font-family: $vf-font-body;
      font-size: 11.5px;
      padding: 0 7px;
      outline: none;

      &::placeholder { color: $vf-sb-ink-3; }
      &:focus { border-color: $vf-sb-led-ready; }
    }

    .scan-filter-count {
      flex: 0 0 auto;
      font-family: $vf-font-mono;
      font-size: 9.5px;
      letter-spacing: 0.08em;
      color: $vf-sb-ink-2;
    }

    /* The whole point of the cap: 87 unique scans rendered unbounded produced a
       menu taller than the screen. */
    .sb-scan-scroll {
      max-height: 300px;
      overflow-y: auto;
      overscroll-behavior: contain;
      padding: 4px;

      &::-webkit-scrollbar { width: 8px; }
      &::-webkit-scrollbar-thumb {
        background: rgba(0, 0, 0, 0.22);
        border-radius: 4px;
      }
      &::-webkit-scrollbar-track { background: transparent; }
    }

    /* No leading glyph column here (unlike the view menu), and the name must
       ellipsize rather than wrap — wrapped names collided with the badges. */
    .sb-menu-item {
      grid-template-columns: minmax(0, 1fr) auto auto;
      gap: 6px;
      color: $vf-sb-ink-1;

      .mname {
        color: $vf-sb-ink-1;
        overflow: hidden;
        text-overflow: ellipsis;
        white-space: nowrap;
      }

      .msub {
        color: $vf-sb-ink-2;
        overflow: hidden;
        text-overflow: ellipsis;
        white-space: nowrap;
        max-width: 130px;
      }

      &:hover {
        background: rgba(0, 0, 0, 0.06);
        color: $vf-sb-ink-1;
      }

      &.on {
        background: rgba(20, 122, 96, 0.14);
        color: $vf-sb-ink-1;

        .mname { color: $vf-sb-ink-1; }
        .msub  { color: $vf-sb-led-ready; }
      }
    }

    .sb-menu-divider {
      background: rgba(0, 0, 0, 0.1);
    }

    .scan-group-cap {
      display: flex;
      align-items: baseline;
      gap: 6px;
      padding: 7px 8px 3px;
      font-family: $vf-font-mono;
      font-size: 9px;
      letter-spacing: 0.12em;
      text-transform: uppercase;
      color: $vf-sb-ink-2;
    }

    .scan-group-note {
      text-transform: none;
      letter-spacing: 0.04em;
      font-size: 9px;
      color: $vf-sb-ink-3;
    }

    /* PROJECT = 360-degree coverage, the safe default. OLD = superseded
       revision, selectable on purpose but never silently. */
    .scan-tag {
      font-family: $vf-font-mono;
      font-size: 8.5px;
      letter-spacing: 0.1em;
      padding: 1px 4px;
      border-radius: 2px;
      white-space: nowrap;
    }

    .scan-tag--project {
      background: rgba(20, 122, 96, 0.16);
      color: $vf-sb-led-ready;
    }

    .scan-tag--old {
      background: rgba(160, 122, 0, 0.16);
      color: $vf-sb-led-busy;
    }

    .scan-empty {
      padding: 14px 10px;
      text-align: center;
      font-family: $vf-font-body;
      font-size: 11.5px;
      color: $vf-sb-ink-2;
    }
  }
}

.sb-marker-popover {
  border-radius: 6px !important;
  background: transparent !important;
  box-shadow: 0 12px 28px rgba(0, 0, 0, 0.24) !important;
  overflow: visible !important;
}

.sb-marker-menu {
  width: 292px;
  padding: 12px;
  border: 1px solid rgba(0, 0, 0, 0.16);
  border-radius: 6px;
  background: $vf-panel-2;
  color: $vf-sb-ink-1;
  font-family: $vf-font-body;

  &__heading {
    display: flex;
    align-items: flex-start;
    justify-content: space-between;
    gap: 12px;
    padding-bottom: 9px;
    border-bottom: 1px solid rgba(0, 0, 0, 0.1);

    > span {
      display: flex;
      flex-direction: column;
    }

    strong {
      font-size: 12px;
      font-weight: 600;
    }

    small {
      margin-top: 2px;
      color: $vf-sb-ink-2;
      font-size: 10px;
    }
  }

  &__options {
    display: grid;
    grid-template-columns: repeat(3, 1fr);
    gap: 5px;
    padding: 10px 0;

    label {
      display: flex;
      align-items: center;
      gap: 5px;
      color: $vf-sb-ink-1;
      cursor: pointer;
      font-size: 11px;
    }
  }

  &__row {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 10px;
    padding: 8px 0;
    border-top: 1px solid rgba(0, 0, 0, 0.08);
  }

  &__caption {
    color: $vf-sb-ink-2;
    font-family: $vf-font-mono;
    font-size: 9px;
    letter-spacing: 0.1em;
    text-transform: uppercase;
  }

  input[type='checkbox'] {
    accent-color: $vf-primary;
  }
}

.sb-marker-switch {
  display: flex;
  align-items: center;
  gap: 5px;
  cursor: pointer;
  font-family: $vf-font-mono;
  font-size: 9.5px;
  font-weight: 600;
}

.sb-marker-scope {
  display: inline-flex;
  overflow: hidden;
  border: 1px solid rgba(0, 0, 0, 0.14);
  border-radius: 3px;
  width: fit-content;

  button {
    min-height: 25px;
    padding: 0 8px;
    border: 0;
    background: transparent;
    color: $vf-sb-ink-2;
    cursor: pointer;
    font-family: $vf-font-body;
    font-size: 10.5px;

    & + button {
      border-left: 1px solid rgba(0, 0, 0, 0.12);
    }

    &.on {
      background: rgba(20, 122, 96, 0.13);
      color: $vf-primary;
      font-weight: 600;
    }
  }
}

.sb-marker-opacity {
  display: grid;
  gap: 7px;
  padding-top: 8px;
  border-top: 1px solid rgba(0, 0, 0, 0.08);

  > span {
    display: flex;
    align-items: center;
    justify-content: space-between;
  }

  strong {
    font-family: $vf-font-mono;
    font-size: 10px;
  }

  input {
    width: 100%;
    accent-color: $vf-primary;
  }
}

.sb-layer-menu {
  width: 316px;
  padding: 12px;
  border: 1px solid rgba(0, 0, 0, 0.16);
  border-radius: 6px;
  background: $vf-panel-2;
  color: $vf-sb-ink-1;
  font-family: $vf-font-body;

  &__heading {
    display: flex;
    align-items: flex-start;
    justify-content: space-between;
    gap: 12px;
    padding-bottom: 9px;
    border-bottom: 1px solid rgba(0, 0, 0, 0.1);

    > span {
      display: flex;
      flex-direction: column;
    }

    strong {
      font-size: 12px;
      font-weight: 600;
    }

    small {
      max-width: 190px;
      margin-top: 2px;
      color: $vf-sb-ink-2;
      font-size: 10px;
      line-height: 1.3;
    }
  }

  &__row {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 10px;
    padding: 9px 0;
    border-top: 1px solid rgba(0, 0, 0, 0.08);

    &--stack {
      align-items: stretch;
      flex-direction: column;
    }
  }

  &__options {
    display: grid;
    grid-template-columns: repeat(2, minmax(0, 1fr));
    gap: 7px 10px;
    padding: 9px 0;
    border-top: 1px solid rgba(0, 0, 0, 0.08);

    label {
      display: flex;
      align-items: center;
      gap: 5px;
      color: $vf-sb-ink-1;
      cursor: pointer;
      font-size: 10.5px;

      &:last-child {
        grid-column: 1 / -1;
      }
    }
  }

  &__caption {
    color: $vf-sb-ink-2;
    font-family: $vf-font-mono;
    font-size: 9px;
    letter-spacing: 0.1em;
    text-transform: uppercase;
  }

  input[type='checkbox'],
  input[type='radio'],
  input[type='range'] {
    accent-color: $vf-primary;
  }

  input[type='color'] {
    width: 34px;
    height: 24px;
    padding: 2px;
    border: 1px solid rgba(0, 0, 0, 0.16);
    border-radius: 3px;
    background: $vf-sb-btn-bg;
    cursor: pointer;

    &:disabled {
      cursor: not-allowed;
      opacity: 0.42;
    }
  }
}

.sb-layer-slider {
  display: grid;
  gap: 7px;
  padding: 9px 0;

  > span {
    display: flex;
    align-items: center;
    justify-content: space-between;
  }

  strong {
    font-family: $vf-font-mono;
    font-size: 10px;
  }

  input {
    width: 100%;
  }
}

.sb-layer-check,
.sb-color-control {
  display: flex;
  align-items: center;
  gap: 6px;
  cursor: pointer;
  font-size: 10.5px;
}

.sb-layer-reset {
  width: 100%;
  min-height: 27px;
  margin-top: 2px;
  border: 1px solid rgba(0, 0, 0, 0.14);
  border-radius: 3px;
  background: transparent;
  color: $vf-sb-ink-2;
  cursor: pointer;
  font-family: $vf-font-mono;
  font-size: 9px;
  letter-spacing: 0.08em;
  text-transform: uppercase;

  &:hover {
    border-color: $vf-primary;
    color: $vf-primary;
  }
}
</style>
